import { memo, useCallback, useContext, useEffect, useRef, useState } from "react";
import { VariableSizeList } from "react-window";
import CalendarListItem from "app/pages/.shared/CalendarList/CalendarListItem";
import memoizeOne from "memoize-one";
import PropTypes from "prop-types";
import WeekDays from "app/pages/PrePackage/components/DatePickerCalendar/WeekDays";
import { getWeeksInMonth } from "date-fns";
import "./CalendarList.scss";
import indexOf from "lodash/indexOf";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import useOnDir from "app/utils/hooks/useOnDir";

const createItemData = memoizeOne(
	(
		months,
		selectedDate,
		handleDateClick,
		departureDateMin,
		departureDateMax,
		displayCalendarIcon,
		checkDateAvailability
	) => ({
		months,
		selectedDate,
		handleDateClick,
		departureDateMin,
		departureDateMax,
		displayCalendarIcon,
		checkDateAvailability,
	})
);

const itemKey = (index, data) => {
	const item = data.months[index];
	return item;
};

const CalendarList = ({
	onDateSelect,
	months = [],
	selectedDate = [],
	departureDateMin,
	departureDateMax,
	heightOffset,
	displayCalendarIcon = true,
	checkDateAvailability = () => {},
	isCompact = false,
}) => {
	const outerRef = useRef(null);
	const [windowHeight, setWindowHeight] = useState(window.innerHeight);
	const direction = useOnDir();

	const handleDateClick = clickedDate => {
		onDateSelect(clickedDate);
	};

	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL;

	useEffect(() => {
		const el = outerRef.current;
		if (el) {
			let topPosition = 0;
			const dateToCenter = selectedDate[0] || new Date();

			const monthAndYear = `${dateToCenter.getMonth()}/${dateToCenter.getFullYear()}`;
			const monthIndex = indexOf(months, monthAndYear);

			months.forEach((month, index) => {
				if (index < monthIndex) {
					topPosition = topPosition + getItemSize(index);
				}
			});

			el.scrollTo({
				behavior: "auto",
				top: topPosition,
			});
		}
	}, []);

	useEffect(() => {
		const handleResize = () => {
			setWindowHeight(window.innerHeight);
		};

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	const itemData = createItemData(
		months,
		selectedDate,
		handleDateClick,
		departureDateMin,
		departureDateMax,
		displayCalendarIcon,
		checkDateAvailability
	);

	// 270px is the height of all components in the side panel except the datepicker calendar
	const calendarContainerHeight = windowHeight - heightOffset;

	const getItemSize = useCallback(
		index => {
			const month = months[index];

			if (month) {
				const monthValue = Number(month.split("/")[0]);
				const yearValue = Number(month.split("/")[1]);
				const date = new Date(yearValue, monthValue, 15, 15, 0, 0);
				const weeksInMonth = getWeeksInMonth(date, { weekStartsOn: 1 });

				// isCompact tiles are narrower than mobile's (card width / 7 instead of viewport
				// width / 7), so they're shorter too (aspect-ratio: 1/1) — reusing mobile's 400/430
				// leaves a large gap after the actual (shorter) content. At 332px wide,
				// div.react-calendar__month-view__days measures 277.11 for a 5-week month and
				// 332.53 for a 6-week one. +16px on top of the measured content height for
				// breathing room between months.
				if (isCompact) {
					return weeksInMonth === 6 ? 384 : 320;
				}

				if (weeksInMonth === 6) {
					return isMobile ? 430 : (windowHeight + 40) / 1.6;
				}

				return isMobile ? 400 : windowHeight / 1.6;
			}
			return 0;
		},
		[months, isMobile, isCompact, windowHeight]
	);

	return (
		<div className="date-picker-calendar">
			<WeekDays />
			<VariableSizeList
				outerRef={outerRef}
				overscanCount={1}
				itemCount={months.length}
				height={calendarContainerHeight}
				estimatedItemSize={375}
				itemSize={getItemSize}
				itemData={itemData}
				itemKey={itemKey}
				direction={direction}
			>
				{CalendarListItem}
			</VariableSizeList>
		</div>
	);
};

CalendarList.propTypes = {
	onDateSelect: PropTypes.func,
	months: PropTypes.array,
	selectedDate: PropTypes.array,
	displayCalendarIcon: PropTypes.bool,
	departureDateMin: PropTypes.number,
	departureDateMax: PropTypes.number,
	heightOffset: PropTypes.number,
	checkDateAvailability: PropTypes.func,
	isCompact: PropTypes.bool,
};

export default memo(CalendarList);
